/**
 * CountdownOverlay — self-contained centered countdown label.
 *
 * Owns everything related to running a countdown:
 *   - the 1-Hz tick that decrements the integer between server broadcasts
 *   - the DOM show/hide state
 *   - the pulse tween per integer change
 *   - the fade-out on stop
 *
 * Callers just tell it `start(msRemaining)` and `stop()`. It's idempotent:
 *   - calling start() while already running just re-syncs to the new value
 *     and restarts the local tick (no hide/show dance, no tween race).
 *   - calling stop() when not running is a no-op.
 *
 * Used by the STARTING state via context methods on GameFlowController. The
 * state machine never has to think about visibility or interval management.
 */

import { gsap } from 'gsap';

export interface CountdownOverlayOptions {
  container: HTMLElement;
}

export class CountdownOverlay {
  private root: HTMLElement;
  private labelEl: HTMLElement;
  private numberEl: HTMLElement;

  private isActive = false;
  private lastValue: number | null = null;
  private remaining = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private numberTween: gsap.core.Tween | null = null;

  constructor(options: CountdownOverlayOptions) {
    CountdownOverlay.injectStyles();

    this.root = document.createElement('div');
    this.root.className = 'countdown-overlay';
    this.root.style.display = 'none';

    this.labelEl = document.createElement('div');
    this.labelEl.className = 'countdown-overlay__label';
    this.labelEl.textContent = 'GAME STARTS IN';

    this.numberEl = document.createElement('div');
    this.numberEl.className = 'countdown-overlay__number';

    this.root.appendChild(this.labelEl);
    this.root.appendChild(this.numberEl);
    options.container.appendChild(this.root);
  }

  /**
   * Sync the overlay to the server's remaining time. Starts the countdown if
   * not already running; otherwise just updates the integer and restarts the
   * 1-Hz local tick so drift stays bounded to one server broadcast cycle.
   *
   * Pass `timeRemainingMs <= 0` to hide — equivalent to stop().
   */
  start(timeRemainingMs: number, label?: string): void {
    if (label !== undefined && this.labelEl.textContent !== label) {
      this.labelEl.textContent = label;
    }

    const remaining = Math.max(0, Math.ceil(timeRemainingMs / 1000));
    if (remaining <= 0) {
      this.stop();
      return;
    }

    // Kill any pending fade-out tween so a previous stop() can't clobber us.
    gsap.killTweensOf(this.root);

    if (!this.isActive) {
      this.isActive = true;
      this.root.style.display = 'flex';
      this.lastValue = null;
    }
    this.root.style.opacity = '1';

    this.remaining = remaining;
    this.setNumber(remaining);
    this.restartTicker();
  }

  /**
   * Stop the countdown and fade the overlay out. Idempotent.
   */
  stop(): void {
    this.clearTicker();
    if (!this.isActive) return;
    this.isActive = false;
    this.numberTween?.kill();
    this.numberTween = null;
    gsap.to(this.root, {
      opacity: 0,
      duration: 0.25,
      ease: 'power2.in',
      onComplete: () => {
        this.root.style.display = 'none';
        this.root.style.opacity = '1';
        this.lastValue = null;
      },
    });
  }

  destroy(): void {
    this.clearTicker();
    this.numberTween?.kill();
    gsap.killTweensOf(this.root);
    this.root.remove();
  }

  // ── internals ──────────────────────────────────────────────────────

  private restartTicker(): void {
    this.clearTicker();
    this.timer = setInterval(() => {
      this.remaining -= 1;
      if (this.remaining <= 0) {
        this.stop();
        return;
      }
      this.setNumber(this.remaining);
    }, 1000);
  }

  private clearTicker(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private setNumber(n: number): void {
    if (this.lastValue === n) return;
    this.lastValue = n;
    this.numberEl.textContent = String(n);
    this.numberTween?.kill();
    this.numberTween = gsap.fromTo(
      this.numberEl,
      { scale: 1.4, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.35, ease: 'back.out(1.7)' }
    );
  }

  private static stylesInjected = false;
  private static injectStyles(): void {
    if (CountdownOverlay.stylesInjected) return;
    CountdownOverlay.stylesInjected = true;
    const style = document.createElement('style');
    style.textContent = `
      .countdown-overlay {
        position: fixed;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        z-index: 60;
        font-family: 'Bebas Neue', 'Rajdhani', sans-serif;
        text-align: center;
      }
      .countdown-overlay__label {
        font-size: clamp(22px, 4vw, 40px);
        letter-spacing: 0.14em;
        color: #FFD700;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.85);
        margin-bottom: 12px;
      }
      .countdown-overlay__number {
        font-size: clamp(128px, 22vw, 240px);
        line-height: 1;
        color: #FFFFFF;
        text-shadow:
          0 0 44px rgba(255, 215, 0, 0.55),
          0 4px 20px rgba(0, 0, 0, 0.9);
        will-change: transform, opacity;
      }
    `;
    document.head.appendChild(style);
  }
}
